// Standard Treatment API — endpoint & body mengikuti openapi.yaml / HANDOFF.md.
// Semua method me-resolve `res.data.data` (isi envelope), sama seperti service lain.
import api from "utils/api";
import { errHandler } from "utils/common";

const request = (method, url, { params, body } = {}) =>
  new Promise((resolve, reject) => {
    api
      .request({ method, url: `/api${url}`, params, data: body })
      .then((res) => resolve(res.data.data))
      .catch((err) => {
        // HANDOFF §9: 401 "Access Denied" = role tidak diizinkan, BUKAN sesi habis →
        // jangan logout; tampilkan pesannya. 401 lain (token tidak valid) → logout seperti biasa.
        if (err?.response?.data?.message !== "Access Denied") errHandler(err);
        reject(err);
      });
  });

export class StandardTreatmentApi {
  // Versions
  listVersions = (params) => request("get", "/version-rules", { params });
  createVersion = () => request("post", "/version-rule", { body: {} });
  getVersion = (id) => request("get", `/version-rule/${id}`);
  deleteVersion = (id) => request("delete", `/version-rule/${id}`);
  cloneVersion = (id) => request("post", `/version-rule/${id}/clone`);
  getValidation = (id) => request("get", `/version-rule/${id}/validation`);
  archiveVersion = (id) => request("post", `/version-rule/${id}/archive`);
  unarchiveVersion = (id) => request("post", `/version-rule/${id}/unarchive`);

  // Releases
  releaseVersion = (id, body) => request("post", `/version-rule/${id}/release`, { body });
  getActiveRelease = () => request("get", "/version-release/active");
  listReleases = (params) => request("get", "/version-releases", { params });
  getRelease = (id) => request("get", `/version-release/${id}`);

  // Rules
  listRules = (params) => request("get", "/standard-treatment-rules", { params });
  getRulesSummary = (version_id) =>
    request("get", "/standard-treatment-rules/summary", { params: { version_id } });
  getRule = (id) => request("get", `/standard-treatment-rule/${id}`);
  createRule = (body) => request("post", "/standard-treatment-rule", { body });
  updateRule = (id, body) => request("patch", `/standard-treatment-rule/${id}`, { body });
  deleteRule = (id) => request("delete", `/standard-treatment-rule/${id}`);
  replaceConditions = (id, conditions) =>
    request("put", `/standard-treatment-rule/${id}/conditions`, { body: { conditions } });
  replaceTherapies = (id, therapies) =>
    request("put", `/standard-treatment-rule/${id}/therapies`, { body: { therapies } });

  // Master data
  listDrugs = (params) => request("get", "/drugs", { params });
  getDrug = (id) => request("get", `/drug/${id}`);
  createDrug = (body) => request("post", "/drug", { body });
  updateDrug = (id, body) => request("patch", `/drug/${id}`, { body });
  listRegimens = (params) => request("get", "/regimens", { params });
  getRegimen = (id) => request("get", `/regimen/${id}`);
  createRegimen = (body) => request("post", "/regimen", { body });
  updateRegimen = (id, body) => request("patch", `/regimen/${id}`, { body });
  listDoseBands = (params) => request("get", "/regimen-dose-bands", { params });
  getDoseBand = (id) => request("get", `/regimen-dose-band/${id}`);
  createDoseBand = (body) => request("post", "/regimen-dose-band", { body });
  updateDoseBand = (id, body) => request("patch", `/regimen-dose-band/${id}`, { body });
  deleteDoseBand = (id) => request("delete", `/regimen-dose-band/${id}`);

  // Simulation
  simulate = (body) => request("post", "/standard-treatment/simulate", { body });

  // Evaluation — pakai snapshot rilis aktif; versionId TIDAK dikirim (ditolak 400).
  evaluate = (body) => request("post", "/standard-treatment/evaluate", { body });
}

const standardTreatmentService = new StandardTreatmentApi();

export default standardTreatmentService;

// Pesan error siap tampil (HANDOFF §3 & §9): selalu `message`, dan `data` HANYA bila
// berupa array string. Bentuk lain (mis. { validationError }) tidak ditampilkan; objek
// release berisi `findings` ditangani terpisah oleh ReleaseModal.
export const errorMessages = (err) => {
  const body = err?.response?.data;
  if (!body) return ["Tidak dapat terhubung ke server"];
  const { message, data } = body;
  const details = Array.isArray(data) && data.every((d) => typeof d === "string") ? data : [];
  const base = message === "Access Denied" ? "Access Denied — peran Anda tidak diizinkan (butuh Admin)" : message;
  return [base || "Terjadi kesalahan", ...details];
};
